import React, { useState, useEffect } from "react";
import { useUserAuth } from "../../context/userAuthContext";
import {
    Users,
    Search,
    X,
    Shield,
    Edit,
    Save,
    Loader,
    CheckCircle2,
    XCircle,
    UserCog,
    AlertCircle,
} from "lucide-react";

const UserManager = () => {
    const { getAllUsers, updateUserRole, user: currentUser } = useUserAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [editingUser, setEditingUser] = useState(null);
    const [saving, setSaving] = useState(false);

    const roles = [
        {
            value: "admin",
            label: "Administrador",
            description: "Acceso completo al sistema",
            color: "red",
        },
        {
            value: "gerente",
            label: "Gerente",
            description: "Gestión de sucursales, productos e inventario",
            color: "purple",
        },
        {
            value: "cajero",
            label: "Cajero",
            description: "Procesamiento de pedidos únicamente",
            color: "blue",
        },
        {
            value: "cliente",
            label: "Cliente",
            description: "Acceso básico a la aplicación",
            color: "green",
        },
    ];

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const usersData = await getAllUsers();
            // Ordenar por rol: admin, gerente, cajero, cliente
            const roleOrder = { admin: 0, gerente: 1, cajero: 2, cliente: 3 };
            usersData.sort((a, b) => {
                const roleA = roleOrder[a.role || a.type] ?? 999;
                const roleB = roleOrder[b.role || b.type] ?? 999;
                return roleA - roleB;
            });
            setUsers(usersData);
        } catch (error) {
            console.error("Error fetching users:", error);
            alert("Error al cargar los usuarios");
        } finally {
            setLoading(false);
        }
    };

    const handleEditUser = (user) => {
        setEditingUser({
            ...user,
            newRole: user.role || user.type || "cliente",
        });
    };

    const handleCancelEdit = () => {
        setEditingUser(null);
    };

    const handleSaveRole = async () => {
        if (!editingUser) return;

        // No permitir que un usuario se cambie su propio rol
        if (editingUser.uid === currentUser?.uid) {
            alert("No puedes cambiar tu propio rol");
            return;
        }

        if (editingUser.newRole === (editingUser.role || editingUser.type)) {
            setEditingUser(null);
            return;
        }

        const confirmMessage = `¿Estás seguro de cambiar el rol de ${editingUser.nombre || editingUser.email
            } a "${roles.find((r) => r.value === editingUser.newRole)?.label}"?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setSaving(true);
        try {
            await updateUserRole(editingUser.uid, editingUser.newRole);
            alert("Rol actualizado exitosamente");
            setEditingUser(null);
            fetchUsers();
        } catch (error) {
            console.error("Error updating role:", error);
            alert("Error al actualizar el rol");
        } finally {
            setSaving(false);
        }
    };

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case "admin":
                return "bg-red-100 text-red-700 border-red-200";
            case "gerente":
                return "bg-purple-100 text-purple-700 border-purple-200";
            case "cajero":
                return "bg-blue-100 text-blue-700 border-blue-200";
            case "cliente":
                return "bg-green-100 text-green-700 border-green-200";
            default:
                return "bg-gray-100 text-gray-700 border-gray-200";
        }
    };

    const getPermissionsList = (role) => {
        const permissions = {
            admin: [
                "Acceso al panel de administración",
                "Gestión de usuarios y permisos",
                "Gestión de productos",
                "Gestión de sucursales",
                "Ver reportes",
                "Procesar pedidos",
            ],
            gerente: [
                "Acceso al panel de administración",
                "Gestión de productos",
                "Gestión de sucursales",
                "Ver reportes",
                "Procesar pedidos",
            ],
            cajero: ["Acceso al panel de administración", "Procesar pedidos"],
            cliente: ["Acceso básico a la aplicación"],
        };
        return permissions[role] || [];
    };

    const filteredUsers = users.filter(
        (user) =>
            user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.nombre?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.role?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.type?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Estadísticas
    const stats = {
        total: users.length,
        admins: users.filter((u) => (u.role || u.type) === "admin").length,
        gerentes: users.filter((u) => (u.role || u.type) === "gerente").length,
        cajeros: users.filter((u) => (u.role || u.type) === "cajero").length,
        clientes: users.filter((u) => (u.role || u.type) === "cliente").length,
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <Loader className="w-12 h-12 text-[#FF0000] animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Cargando usuarios...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h2 className="text-2xl font-bold text-gray-900">Gestión de Usuarios y Permisos</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Administra roles y permisos de {users.length} usuario{users.length !== 1 ? "s" : ""}
                </p>
            </div>

            {/* Warning Banner */}
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div>
                    <p className="text-sm font-semibold text-orange-900">Advertencia</p>
                    <p className="text-sm text-orange-700 mt-1">
                        Cambiar los roles de los usuarios afecta sus permisos de acceso al sistema. Asegúrate de asignar el rol correcto.
                    </p>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="bg-red-50 p-2 rounded-lg">
                            <Shield className="w-5 h-5 text-red-600" strokeWidth={2} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-gray-900">{stats.admins}</p>
                            <p className="text-xs text-gray-500">Admins</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="bg-purple-50 p-2 rounded-lg">
                            <UserCog className="w-5 h-5 text-purple-600" strokeWidth={2} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-gray-900">{stats.gerentes}</p>
                            <p className="text-xs text-gray-500">Gerentes</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="bg-blue-50 p-2 rounded-lg">
                            <Users className="w-5 h-5 text-blue-600" strokeWidth={2} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-gray-900">{stats.cajeros}</p>
                            <p className="text-xs text-gray-500">Cajeros</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="bg-green-50 p-2 rounded-lg">
                            <Users className="w-5 h-5 text-green-600" strokeWidth={2} />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-gray-900">{stats.clientes}</p>
                            <p className="text-xs text-gray-500">Clientes</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3">
                    <Search className="w-5 h-5 text-gray-400" strokeWidth={2} />
                    <input
                        type="text"
                        placeholder="Buscar por nombre, email o rol..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 bg-transparent text-gray-800 text-sm font-medium placeholder-gray-400 focus:outline-none"
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery("")}
                            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4 text-gray-600" />
                        </button>
                    )}
                </div>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                                    Usuario
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                                    Email
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                                    Rol Actual
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                                    Estado
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredUsers.length > 0 ? (
                                filteredUsers.map((user) => {
                                    const isEditing = editingUser?.uid === user.uid;
                                    const userRole = user.role || user.type || "cliente";
                                    const isCurrentUser = user.uid === currentUser?.uid;

                                    return (
                                        <tr key={user.uid} className="hover:bg-gray-50">
                                            <td className="px-4 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF0000] to-[#CC0000] flex items-center justify-center text-white font-bold">
                                                        {(user.nombre || user.email)?.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-gray-900">
                                                            {user.nombre || "Sin nombre"}
                                                            {isCurrentUser && (
                                                                <span className="ml-2 text-xs text-[#FF0000] font-semibold">
                                                                    (Tú)
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            UID: {user.uid?.substring(0, 8)}...
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className="text-sm text-gray-600">{user.email}</span>
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                {isEditing ? (
                                                    <select
                                                        value={editingUser.newRole}
                                                        onChange={(e) =>
                                                            setEditingUser({
                                                                ...editingUser,
                                                                newRole: e.target.value,
                                                            })
                                                        }
                                                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF0000]"
                                                        disabled={saving}
                                                    >
                                                        {roles.map((role) => (
                                                            <option key={role.value} value={role.value}>
                                                                {role.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span
                                                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getRoleBadgeColor(
                                                            userRole
                                                        )}`}
                                                    >
                                                        {roles.find((r) => r.value === userRole)?.label || userRole}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4 text-center">
                                                {user.isActive !== false ? (
                                                    <span className="inline-flex items-center gap-1 text-green-600 text-sm font-semibold">
                                                        <CheckCircle2 className="w-4 h-4" strokeWidth={2} />
                                                        Activo
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 text-red-600 text-sm font-semibold">
                                                        <XCircle className="w-4 h-4" strokeWidth={2} />
                                                        Inactivo
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex items-center justify-center gap-2">
                                                    {isEditing ? (
                                                        <>
                                                            <button
                                                                onClick={handleSaveRole}
                                                                disabled={saving}
                                                                className="p-2 bg-green-50 text-green-600 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-50"
                                                                title="Guardar"
                                                            >
                                                                {saving ? (
                                                                    <Loader className="w-4 h-4 animate-spin" />
                                                                ) : (
                                                                    <Save className="w-4 h-4" strokeWidth={2} />
                                                                )}
                                                            </button>
                                                            <button
                                                                onClick={handleCancelEdit}
                                                                disabled={saving}
                                                                className="p-2 bg-gray-100 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                                                                title="Cancelar"
                                                            >
                                                                <X className="w-4 h-4" strokeWidth={2} />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleEditUser(user)}
                                                            disabled={isCurrentUser}
                                                            className="p-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                            title={isCurrentUser ? "No puedes editar tu propio rol" : "Editar rol"}
                                                        >
                                                            <Edit className="w-4 h-4" strokeWidth={2} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                        No se encontraron usuarios
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Roles Info */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Información de Roles y Permisos</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {roles.map((role) => (
                        <div key={role.value} className="border border-gray-200 rounded-xl p-4">
                            <div className="flex items-center gap-3 mb-3">
                                <span
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getRoleBadgeColor(
                                        role.value
                                    )}`}
                                >
                                    {role.label}
                                </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{role.description}</p>
                            <div className="space-y-2">
                                {getPermissionsList(role.value).map((permission, index) => (
                                    <div key={index} className="flex items-start gap-2">
                                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" strokeWidth={2} />
                                        <span className="text-xs text-gray-600">{permission}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default UserManager;


